import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Button,
  Grid,
  Card,
  CardActionArea,
  TextField,
  CircularProgress,
  Chip,
  Avatar,
  Divider,
  Alert,
  Stack,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import {
  CheckCircle,
  AccessTime,
  Person,
  ContentCut,
  CalendarToday,
  Phone as PhoneIcon,
  Email as EmailIcon,
  PersonSearch,
  ArrowBack,
  ArrowForward
} from '@mui/icons-material';
import {
  getPublicServiceList,
  getPublicAvailableSlots,
  getPublicServiceProviders,
  createPublicBooking
} from '../../api/services/clientBooking.service';

const steps = ['Select Services', 'Choose Provider', 'Pick Date & Time', 'Your Info', 'Confirm'];

/**
 * Client-facing booking page for customers to book appointments
 * 
 * URL Parameters:
 * - store: (required) Store ID for the business location
 * - provider: (optional) Provider ID to preselect a specific service provider
 * 
 * Examples:
 * - Basic booking: /salcli?store=SZG104000
 * - With preselected provider: /salcli?store=SZG104000&provider=66b100e4e2410a88
 * 
 * Features:
 * - 5-step booking flow: Services → Provider → Date/Time → Contact Info → Confirm
 * - Real-time slot availability
 * - Past date/time validation
 * - Provider preselection via URL
 * - Responsive Material-UI design
 */
export const ClientBooking = () => {
  // Get storeID and providerID from URL params (e.g., /book?store=SZG104000&provider=66b100e4e2410a88)
  const urlParams = new URLSearchParams(window.location.search);
  const storeID = urlParams.get('store') || JSON.parse(window.localStorage.getItem('ds'));
  const preselectedProviderID = urlParams.get('provider'); // Optional provider preselection

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successData, setSuccessData] = useState(null);

  // Form selections
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);

  const [providerMode, setProviderMode] = useState('any_available'); // 'any_available' or 'specific'
  const [providers, setProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [providerPreselected, setProviderPreselected] = useState(false); // Track if provider was preselected from URL
  const [showProviderList, setShowProviderList] = useState(false); // Control visibility of provider list

  const [selectedDate, setSelectedDate] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState('');

  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    email: '',
    notes: ''
  });

  // Fetch available services on mount
  useEffect(() => {
    const fetchServices = async () => {
      if (!storeID) return;

      setLoadingServices(true);
      try {
        const response = await getPublicServiceList({ storeID, onlineOnly: true });

        if (response && response.code === 0 && response.data) {
          const transformedServices = response.data.map(service => ({
            id: service._id,
            name: service.item,
            duration: service.serviceMeta?.durationMinutes || 30,
            price: service.price || 0,
            category: service.category || 'General',
            description: service.description || ''
          }));
          setServices(transformedServices);
        }
      } catch (error) {
        setErrorMsg('Failed to load services. Please refresh the page.');
      } finally {
        setLoadingServices(false);
      }
    };

    fetchServices();
  }, [storeID]);

  // Fetch providers when needed
  useEffect(() => {
    const fetchProviders = async () => {
      if (!storeID) return;

      // Fetch providers if:
      // 1. In specific mode, OR
      // 2. A provider is preselected in URL (need to validate it exists)
      const shouldFetchProviders = providerMode === 'specific' || preselectedProviderID;
      
      if (!shouldFetchProviders) return;

      setLoadingProviders(true);
      try {
        const response = await getPublicServiceProviders(storeID);

        if (response && response.code === 0 && response.data) {
          const transformedProviders = response.data.map(provider => ({
            id: provider._id,
            name: provider.providerName,
            station: provider.assignedStation || '',
            avatar: provider.avatar || '',
            isActive: provider.isActive !== false
          }));

          const activeProviders = transformedProviders.filter(p => p.isActive);
          setProviders(activeProviders);

          // If provider ID was passed in URL, auto-select it
          if (preselectedProviderID && !providerPreselected) {
            const matchedProvider = activeProviders.find(p => p.id === preselectedProviderID);
            
            if (matchedProvider) {
              setProviderMode('specific');
              setSelectedProvider(matchedProvider);
              setProviderPreselected(true);
              setShowProviderList(false); // Hide list initially for preselected provider
              console.log('Provider preselected from URL:', matchedProvider.name);
            } else {
              console.warn('Preselected provider ID not found:', preselectedProviderID);
              setShowProviderList(true); // Show list if provider not found
            }
          } else if (!providerPreselected) {
            setShowProviderList(true); // Show list for normal flow
          }
        }
      } catch (error) {
        setErrorMsg('Failed to load service providers.');
      } finally {
        setLoadingProviders(false);
      }
    };

    fetchProviders();
  }, [storeID, providerMode, preselectedProviderID, providerPreselected]);

  // Fetch available time slots when date and services are selected
  useEffect(() => {
    const fetchSlots = async () => {
      if (!storeID || !selectedDate || selectedServices.length === 0) {
        setAvailableSlots([]);
        return;
      }

      setLoadingSlots(true);
      try {
        const serviceIDs = selectedServices.map(s => s.id).join(',');

        const params = {
          storeID,
          bookingType: 'service',
          date: selectedDate,
          serviceIDs
        };

        // Add provider filter if in specific mode
        if (providerMode === 'specific' && selectedProvider) {
          params.providerID = selectedProvider.id;
        }

        const response = await getPublicAvailableSlots(params);

        if (response && response.code === 0 && response.data) {
          const slots = response.data.slots || [];

          // Filter out past time slots for today
          const today = new Date().toISOString().split('T')[0];
          const now = new Date();
          const currentHours = now.getHours();
          const currentMinutes = now.getMinutes();

          const transformedSlots = slots
            .filter(slot => {
              if (selectedDate !== today) return true;

              const [slotHours, slotMinutes] = slot.time.split(':').map(Number);
              if (slotHours < currentHours) return false;
              if (slotHours === currentHours && slotMinutes < currentMinutes) return false;
              return true;
            })
            .map(slot => ({
              time: slot.time || slot.startTime,
              availableCapacity: slot.availableCapacity || slot.availableProviders || 0,
              isAvailable: slot.isAvailable !== false && (slot.availableCapacity || slot.availableProviders || 0) > 0
            }))
            .filter(slot => slot.isAvailable);

          setAvailableSlots(transformedSlots);
        }
      } catch (error) {
        setErrorMsg('Unable to retrieve available time slots.');
      } finally {
        setLoadingSlots(false);
      }
    };

    fetchSlots();
  }, [storeID, selectedDate, selectedServices, providerMode, selectedProvider]);

  // Helper functions
  const toggleService = (service) => {
    const exists = selectedServices.find(s => s.id === service.id);
    if (exists) {
      setSelectedServices(selectedServices.filter(s => s.id !== service.id));
    } else {
      setSelectedServices([...selectedServices, service]);
    }
  };

  const totalDuration = selectedServices.reduce((acc, s) => acc + s.duration, 0);
  const totalPrice = selectedServices.reduce((acc, s) => acc + s.price, 0);

  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getMinDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  const handleBookingSubmit = async () => {
    try {
      setLoading(true);
      setErrorMsg('');

      const bookingData = {
        storeID,
        bookingType: 'service',
        bookingDate: selectedDate,
        startTime: selectedSlot,
        customerName: customerInfo.name,
        customerPhone: customerInfo.phone,
        customerEmail: customerInfo.email,
        notes: customerInfo.notes,
        source: 'online',
        serviceIDs: selectedServices.map(s => s.id),
        providerID: providerMode === 'specific' && selectedProvider ? selectedProvider.id : null
      };

      const response = await createPublicBooking(bookingData);

      if (response && response.code === 0 && response.data) {
        setSuccessData(response.data);
        setActiveStep(5); // Success step
      } else {
        setErrorMsg(response?.msg || 'Booking failed. Please try again.');
      }
    } catch (error) {
      setErrorMsg(error.message || 'Booking failed. The time slot might have just been reserved.');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    setErrorMsg('');
    if (activeStep === 4) {
      handleBookingSubmit();
    } else {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setErrorMsg('');
    setActiveStep(prev => prev - 1);
  };

  const isStepValid = () => {
    switch (activeStep) {
      case 0:
        return selectedServices.length > 0;
      case 1:
        return providerMode === 'any_available' || (providerMode === 'specific' && selectedProvider !== null);
      case 2:
        return selectedDate && selectedSlot;
      case 3:
        return customerInfo.name.trim() && customerInfo.phone.trim();
      case 4:
        return true;
      default:
        return false;
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3 }}>
        <Typography variant="h4" align="center" fontWeight="bold" gutterBottom>
          Book Your Appointment
        </Typography>
        <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 4 }}>
          {preselectedProviderID 
            ? `Booking with ${selectedProvider?.name || 'your selected provider'}`
            : 'Select your services, choose your preferred time, and we\'ll take care of the rest'
          }
        </Typography>

        {activeStep < 5 && (
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {errorMsg && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrorMsg('')}>{errorMsg}</Alert>}

        {/* STEP 0: SELECT SERVICES */}
        {activeStep === 0 && (
          <Box>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ContentCut />
              Select Your Services
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose one or more services you'd like
            </Typography>

            {loadingServices ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={2}>
                {services.map((service) => {
                  const isSelected = selectedServices.some(s => s.id === service.id);
                  return (
                    <Grid item xs={12} sm={6} key={service.id}>
                      <Card
                        variant="outlined"
                        sx={{
                          borderColor: isSelected ? 'primary.main' : 'divider',
                          borderWidth: isSelected ? 2 : 1,
                          bgcolor: isSelected ? 'primary.50' : 'background.paper',
                          transition: 'all 0.2s'
                        }}
                      >
                        <CardActionArea onClick={() => toggleService(service)} sx={{ p: 2.5 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Box>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {service.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                <AccessTime fontSize="inherit" sx={{ mr: 0.5 }} />
                                {service.duration} minutes
                              </Typography>
                            </Box>
                            <Typography variant="h6" color="primary.main" fontWeight="bold">
                              ${service.price}
                            </Typography>
                          </Stack>
                          {isSelected && (
                            <Chip
                              label="Selected"
                              size="small"
                              color="primary"
                              sx={{ mt: 1 }}
                              icon={<CheckCircle fontSize="small" />}
                            />
                          )}
                        </CardActionArea>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}

            {selectedServices.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2, mt: 3, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" fontWeight="bold">Selected Services:</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                  {selectedServices.map(service => (
                    <Chip
                      key={service.id}
                      label={`${service.name} (${service.duration}min - $${service.price})`}
                      onDelete={() => toggleService(service)}
                      color="secondary"
                    />
                  ))}
                </Box>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="body2">
                  <strong>Total Duration:</strong> {totalDuration} minutes | <strong>Total Price:</strong> ${totalPrice}
                </Typography>
              </Paper>
            )}
          </Box>
        )}

        {/* STEP 1: CHOOSE PROVIDER MODE */}
        {activeStep === 1 && (
          <Box>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person />
              Choose Your Booking Preference
            </Typography>

            {/* If provider was preselected, show summary card with change button */}
            {providerPreselected && selectedProvider && !showProviderList ? (
              <Box>
                <Alert severity="success" sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Provider Pre-selected
                  </Typography>
                  <Typography variant="body2">
                    Your appointment is set with <strong>{selectedProvider.name}</strong>. Continue to select your date and time, or change provider below.
                  </Typography>
                </Alert>

                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    mb: 2,
                    borderLeft: 4,
                    borderColor: 'primary.main',
                    bgcolor: 'background.paper'
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: 'primary.main', width: 64, height: 64, fontSize: '1.5rem' }}>
                      {selectedProvider.name[0]}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" fontWeight="bold">
                        {selectedProvider.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedProvider.station || 'Service Provider'}
                      </Typography>
                      <Chip
                        icon={<CheckCircle />}
                        label="Selected"
                        color="success"
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  </Stack>
                </Paper>

                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => setShowProviderList(true)}
                  startIcon={<Person />}
                  sx={{ mt: 1 }}
                >
                  Change Provider
                </Button>
              </Box>
            ) : (
              /* Show full provider selection UI */
              <>
                {providerPreselected && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    You can choose a different provider or continue with your selection.
                  </Alert>
                )}

                <RadioGroup
                  value={providerMode}
                  onChange={(e) => {
                    setProviderMode(e.target.value);
                    if (e.target.value === 'any_available') {
                      setSelectedProvider(null);
                    }
                  }}
                >
                  <Paper variant="outlined" sx={{ p: 2, mb: 2, cursor: 'pointer' }} onClick={() => setProviderMode('any_available')}>
                    <FormControlLabel
                      value="any_available"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="subtitle1" fontWeight="medium">
                            <PersonSearch sx={{ verticalAlign: 'middle', mr: 1 }} />
                            Any Available Provider
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            We'll assign the first available specialist when you arrive. Most flexible option.
                          </Typography>
                        </Box>
                      }
                    />
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 2, cursor: 'pointer' }} onClick={() => setProviderMode('specific')}>
                    <FormControlLabel
                      value="specific"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="subtitle1" fontWeight="medium">
                            <CheckCircle sx={{ verticalAlign: 'middle', mr: 1 }} />
                            Choose Specific Provider
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Select your preferred specialist now. They'll be ready for you.
                          </Typography>
                        </Box>
                      }
                    />
                  </Paper>
                </RadioGroup>

                {/* Show provider selection if specific mode */}
                {providerMode === 'specific' && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Select Your Provider:
                    </Typography>

                    {loadingProviders ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={32} />
                      </Box>
                    ) : (
                      <Grid container spacing={2}>
                        {providers.map((provider) => {
                          const isSelected = selectedProvider?.id === provider.id;
                          return (
                            <Grid item xs={12} sm={6} key={provider.id}>
                              <Card
                                variant="outlined"
                                sx={{
                                  borderColor: isSelected ? 'primary.main' : 'divider',
                                  borderWidth: isSelected ? 2 : 1
                                }}
                              >
                                <CardActionArea onClick={() => setSelectedProvider(provider)} sx={{ p: 2 }}>
                                  <Stack direction="row" spacing={2} alignItems="center">
                                    <Avatar sx={{ bgcolor: 'secondary.main', width: 48, height: 48 }}>
                                      {provider.name[0]}
                                    </Avatar>
                                    <Box>
                                      <Typography variant="subtitle1" fontWeight="bold">
                                        {provider.name}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {provider.station || 'Specialist'}
                                      </Typography>
                                    </Box>
                                  </Stack>
                                </CardActionArea>
                              </Card>
                            </Grid>
                          );
                        })}
                      </Grid>
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>
        )}

        {/* STEP 2: SELECT DATE & TIME */}
        {activeStep === 2 && (
          <Box>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarToday />
              Pick Your Appointment Time
            </Typography>

            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  type="date"
                  label="Select Date"
                  value={selectedDate}
                  onChange={(e) => {
                    setSelectedDate(e.target.value);
                    setSelectedSlot(''); // Reset slot when date changes
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: getMinDate() }}
                  helperText="Cannot select past dates"
                />
              </Grid>

              <Grid item xs={12} sm={7}>
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                  Available Time Slots:
                </Typography>

                {!selectedDate ? (
                  <Alert severity="info">Please select a date first</Alert>
                ) : loadingSlots ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Loading slots...</Typography>
                  </Box>
                ) : availableSlots.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {availableSlots.map(slot => (
                      <Chip
                        key={slot.time}
                        label={formatTime(slot.time)}
                        clickable
                        color={selectedSlot === slot.time ? 'primary' : 'default'}
                        variant={selectedSlot === slot.time ? 'filled' : 'outlined'}
                        onClick={() => setSelectedSlot(slot.time)}
                        sx={{ minWidth: 90 }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Alert severity="warning">
                    No available slots on this date. Please select another date.
                  </Alert>
                )}
              </Grid>
            </Grid>
          </Box>
        )}

        {/* STEP 3: CUSTOMER INFORMATION */}
        {activeStep === 3 && (
          <Box>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person />
              Your Contact Information
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              We'll use this to send you appointment confirmations
            </Typography>

            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Full Name"
                  required
                  value={customerInfo.name}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                  placeholder="John Doe"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  required
                  value={customerInfo.phone}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  InputProps={{
                    startAdornment: <PhoneIcon sx={{ mr: 1, color: 'action.active', fontSize: 20 }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  value={customerInfo.email}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                  placeholder="john@example.com"
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ mr: 1, color: 'action.active', fontSize: 20 }} />
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Special Requests or Notes"
                  value={customerInfo.notes}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, notes: e.target.value })}
                  placeholder="Any special requests or things we should know?"
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* STEP 4: CONFIRMATION SUMMARY */}
        {activeStep === 4 && (
          <Box>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle />
              Review & Confirm Your Booking
            </Typography>

            <Paper variant="outlined" sx={{ p: 3, mt: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" fontWeight="bold" color="primary.main" gutterBottom>
                APPOINTMENT DETAILS
              </Typography>

              <Stack spacing={1.5} sx={{ mt: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Date & Time</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })} at {formatTime(selectedSlot)}
                  </Typography>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="caption" color="text.secondary">Services</Typography>
                  {selectedServices.map(service => (
                    <Typography key={service.id} variant="body2">
                      • {service.name} ({service.duration} min) - ${service.price}
                    </Typography>
                  ))}
                </Box>

                <Divider />

                <Box>
                  <Typography variant="caption" color="text.secondary">Provider</Typography>
                  <Typography variant="body1">
                    {providerMode === 'any_available'
                      ? 'Any Available Provider (assigned on arrival)'
                      : selectedProvider?.name
                    }
                  </Typography>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="caption" color="text.secondary">Contact</Typography>
                  <Typography variant="body2">{customerInfo.name}</Typography>
                  <Typography variant="body2">{customerInfo.phone}</Typography>
                  {customerInfo.email && <Typography variant="body2">{customerInfo.email}</Typography>}
                </Box>

                {customerInfo.notes && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Notes</Typography>
                      <Typography variant="body2">{customerInfo.notes}</Typography>
                    </Box>
                  </>
                )}

                <Divider />

                <Box sx={{ pt: 1 }}>
                  <Typography variant="body2" color="text.secondary">Total Duration</Typography>
                  <Typography variant="h6" fontWeight="bold">{totalDuration} minutes</Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Total Price</Typography>
                  <Typography variant="h5" fontWeight="bold" color="primary.main">
                    ${totalPrice.toFixed(2)}
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            <Alert severity="info" sx={{ mt: 2 }}>
              By confirming this booking, you agree to arrive on time. Cancellations should be made at least 24 hours in advance.
            </Alert>
          </Box>
        )}

        {/* STEP 5: SUCCESS */}
        {activeStep === 5 && successData && (
          <Box textAlign="center" sx={{ py: 4 }}>
            <CheckCircle color="success" sx={{ fontSize: 80, mb: 3 }} />
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Booking Confirmed!
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
              Confirmation Code: <Chip label={successData.bookingCode} color="primary" sx={{ fontSize: '1.1rem', px: 2 }} />
            </Typography>

            <Paper variant="outlined" sx={{ p: 3, maxWidth: 400, mx: 'auto', mb: 3, bgcolor: 'grey.50' }}>
              <Typography variant="body1" gutterBottom>
                <strong>Date:</strong> {new Date(selectedDate + 'T00:00:00').toLocaleDateString()}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Time:</strong> {formatTime(selectedSlot)}
              </Typography>
              <Typography variant="body1">
                <strong>Services:</strong> {selectedServices.map(s => s.name).join(', ')}
              </Typography>
            </Paper>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              A confirmation has been sent to <strong>{customerInfo.phone}</strong>
              {customerInfo.email && <> and <strong>{customerInfo.email}</strong></>}
            </Typography>

            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="contained"
                onClick={() => {
                  setSelectedServices([]);
                  setSelectedProvider(null);
                  setSelectedDate('');
                  setSelectedSlot('');
                  setCustomerInfo({ name: '', phone: '', email: '', notes: '' });
                  setProviderMode('any_available');
                  setActiveStep(0);
                  setSuccessData(null);
                }}
              >
                Book Another Appointment
              </Button>
            </Stack>
          </Box>
        )}

        {/* NAVIGATION BUTTONS */}
        {activeStep < 5 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              startIcon={<ArrowBack />}
              disabled={activeStep === 0}
              onClick={handleBack}
            >
              Back
            </Button>
            <Button
              variant="contained"
              endIcon={activeStep === 4 ? <CheckCircle /> : <ArrowForward />}
              disabled={!isStepValid() || loading}
              onClick={handleNext}
            >
              {activeStep === 4
                ? (loading ? 'Booking...' : 'Confirm Appointment')
                : 'Next'
              }
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default ClientBooking;
